import { useApolloClient } from '@apollo/client';
import { LaborMetricsInterval } from '__generated__/graphql';
import {
  SIMPLE_PROJECT_WITH_EQUIPMENT_METRIC_AND_SUB_CHECK_QUERY,
  SIMPLE_PROJECT_WITH_METRIC_QUERY,
} from 'apollo/queries/project-queries';
import { ArchivedStatus } from 'components/domain/archived/ArchivedPicker/ArchivedPicker';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import useGetProgressBudgetActualsData, {
  IProgressBudgetActuals,
} from 'containers/budgets/CreateBudgetForm/hooks/useGetProgressBudgetActualsData';
import useProgressValueEnabled from 'containers/budgets/CreateBudgetForm/hooks/useProgressValueEnabled';
import useBudgetCostData from 'containers/budgets/hooks/useBudgetCostData';
import useHoursBudgetData from 'containers/budgets/hooks/useHoursBudgetData';
import { useDashboardTimeRange } from 'containers/dashboard/hooks';
import { useToastOpen } from 'contexts/ToastContext';
import { useApolloPaging, useOrganization } from 'hooks';
import { projectLaborMetricQueryProvider } from 'hooks/aggregates/metrics/ProjectMetricQueryProviders';
import useAllTime from 'hooks/utils/useAllTime';
import { Dictionary, groupBy, indexOf, isEmpty, isNil, isNull, orderBy, sumBy } from 'lodash';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import ICursorable from 'types/Cursorable';
import IProject from 'types/Project';
import IJitLaborMetric from 'types/aggregate/JitLaborMetric';
import { getMetricLaborTotals } from 'utils/jitMetricUtils';
import { t } from 'utils/localize';

export interface IBudgetReportDataFilter {
  projectIds?: string[];
  archivedStatus: ArchivedStatus;
  rootProjectsOnly: boolean;
  projectGroupId?: string;
}
export interface IBudgetDashboardCardItem {
  id: string;
  project: IProject;
  budgetSeconds: number | null;
  actualSeconds: number | null;
  actualSecondsForRange: number | null;
  budgetCost: number | null;
  budgetProgressValue: number | null;
  actualProgressValue: number | null;
  actualCost: number | null;
  equipmentBudgetSeconds: number | null;
  equipmentActualSeconds: number | null;
  equipmentBudgetCost: number | null;
  equipmentActualCost: number | null;
  isArchived: boolean;
  sortOrder?: number;
}

export default async function useBudgetDashboardData(projectLimit: number, setting: IDashboardSettingsItem) {
  const toast = useToastOpen();
  const { getAll } = useApolloPaging();
  const client = useApolloClient();
  const { getLatestBudgetCostData } = useBudgetCostData();
  const { getLatestBudgetHoursData } = useHoursBudgetData();
  const timeFrame = setting.options?.time ?? 'pastSeven';
  const timeRange = useDashboardTimeRange(timeFrame, 'system');
  const getAllTime = useAllTime();
  const useCustomIds = setting.options?.projectsToDisplay === 'custom';
  const customIdArray = setting.options?.customProjects
    ? setting.options!.customProjects.filter((p: string) => !isNil(p))
    : null;
  const isProgressValueEnabled = useProgressValueEnabled();
  const getProgressBudgetActualsData = useGetProgressBudgetActualsData();
  const { trackBudgetEquipmentHours, trackBudgetEquipmentCosts } = useOrganization();

  async function projectEquipmentLaborMetrics(projectIds: string[], start: DateTime, end: DateTime) {
    const result = await client.query<{ projects: Array<IProject & ICursorable> }>({
      query: SIMPLE_PROJECT_WITH_EQUIPMENT_METRIC_AND_SUB_CHECK_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          id: { contains: projectIds },
        },
        metricsInterval: LaborMetricsInterval.Custom,
        metricsStartDate: start.toISODate(),
        metricsEndDate: end.toISODate(),
        metricFilter: {
          operationType: 'and',
          nullConditions: { operator: 'isNotNull', field: 'equipmentId' },
        },
      },
    });

    return result.data.projects.flatMap((project) => project.projectEquipmentLaborMetrics ?? []);
  }

  const getProjects = useCallback(async () => {
    let projectResults;
    if (useCustomIds) {
      const customResults = await client.query<{ projects: Array<IProject & ICursorable> }>({
        query: SIMPLE_PROJECT_WITH_METRIC_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          filter: {
            depth: { equal: 1 },
            archivedOn: { isNull: true },
            id: { contains: customIdArray ?? undefined },
          },
          metricsStartDate: timeRange.startTime.toISODate(),
          metricsEndDate: timeRange.endTime.toISODate(),
          metricsInterval: LaborMetricsInterval.Custom,
        },
      });

      projectResults = customResults.data.projects;
    } else {
      const rootProjectResults = await getAll<IProject & ICursorable>('projects', {
        query: SIMPLE_PROJECT_WITH_METRIC_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          first: 500,
          filter: {
            depth: { equal: 1 },
            archivedOn: { isNull: true },
          },
          sort: [{ title: 'asc' }],
          metricsStartDate: timeRange.startTime.toISODate(),
          metricsEndDate: timeRange.endTime.toISODate(),
          metricsInterval: LaborMetricsInterval.Custom,
        },
      });

      projectResults = rootProjectResults.filter((p) => !isEmpty(p.projectLaborMetrics));
    }

    const allProjects = projectResults.map((project) => ({
      id: project.id,
      project,
      actualSecondsForRange: project.projectLaborMetrics
        ? getMetricLaborTotals(project.projectLaborMetrics[0]).totalSeconds
        : 0,
    }));
    const orderedData = orderBy(
      allProjects,
      [(item) => item.actualSecondsForRange, (item) => item.project.title],
      ['desc', 'asc']
    );

    const projects = [];
    for (const p of orderedData) {
      const hoursBudget = await getLatestBudgetHoursData({
        projectId: { equal: p.id },
        costCodeId: { isNull: true },
        equipmentId: { isNull: true },
      });
      const costBudget = await getLatestBudgetCostData({
        projectId: { equal: p.id },
        costCodeId: { isNull: true },
        equipmentId: { isNull: true },
      });

      const budgetItem = {
        id: p.id,
        project: p.project,
        budgetSeconds: !isEmpty(hoursBudget) ? sumBy(hoursBudget, (h) => h.budgetSeconds) : null,
        budgetCost: !isEmpty(costBudget) ? sumBy(costBudget, (h) => h.costBudget ?? 0) : null,
        equipmentBudgetSeconds: !isEmpty(hoursBudget) ? sumBy(hoursBudget, (h) => h.equipmentBudgetSeconds ?? 0) : null,
        equipmentBudgetCost: !isEmpty(costBudget) ? sumBy(costBudget, (h) => h.equipmentCostBudget ?? 0) : null,
        actualSecondsForRange: p.actualSecondsForRange,
      };

      const hasBudgetSet =
        (!isNil(budgetItem.budgetSeconds) || !isNil(budgetItem.budgetCost)) && budgetItem.actualSecondsForRange;

      if (useCustomIds || hasBudgetSet) {
        projects.push(budgetItem);
      }

      // limit queries by exiting when we have filled out limit
      if (projects.length >= projectLimit) {
        break;
      }
    }

    return projects;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAll, timeRange.startTime, timeRange.endTime, useCustomIds, customIdArray]);

  return useCallback(async () => {
    try {
      const projects = await getProjects();
      const projectIds = projects.map((p) => p.id);
      const metricQuery = projectLaborMetricQueryProvider(getAll, projectIds, undefined, 'all');
      const allTime = await getAllTime({ projectIds });
      const metrics = await metricQuery(allTime.startTime, allTime.endTime, LaborMetricsInterval.Custom);
      const groupMetrics = groupBy(metrics, 'projectId');

      let projectEquipmentGroupMetrics: Dictionary<IJitLaborMetric[]> | null = null;

      if (trackBudgetEquipmentHours || trackBudgetEquipmentCosts) {
        const projectEquipmentMetrics = await projectEquipmentLaborMetrics(
          projectIds,
          allTime.startTime,
          allTime.endTime
        );
        projectEquipmentGroupMetrics = groupBy(projectEquipmentMetrics, 'projectId');
      }

      const promises = projects.map(async (p) => {
        let progressBudgetActuals: Map<string, IProgressBudgetActuals> = new Map();
        if (isProgressValueEnabled) {
          progressBudgetActuals = await getProgressBudgetActualsData([p.id], [], true);
        }

        return {
          ...p,
          actualCost: !isEmpty(groupMetrics[p.id])
            ? sumBy(groupMetrics[p.id], (m) => getMetricLaborTotals(m).totalCost)
            : null,
          actualSeconds: !isEmpty(groupMetrics[p.id])
            ? sumBy(groupMetrics[p.id], (m) => getMetricLaborTotals(m).totalSeconds)
            : null,
          budgetProgressValue: progressBudgetActuals.get(p.id)?.budget ?? null,
          actualProgressValue: progressBudgetActuals.get(p.id)?.actual ?? null,
          equipmentActualCost:
            !isNull(projectEquipmentGroupMetrics) && !isEmpty(projectEquipmentGroupMetrics[p.id])
              ? sumBy(projectEquipmentGroupMetrics[p.id], (m) => getMetricLaborTotals(m).equipmentCost)
              : null,
          equipmentActualSeconds:
            !isNull(projectEquipmentGroupMetrics) && !isEmpty(projectEquipmentGroupMetrics[p.id])
              ? sumBy(projectEquipmentGroupMetrics[p.id], (m) => getMetricLaborTotals(m).totalSeconds)
              : null,
          isArchived: !isNil(p.project.archivedOn),
          sortOrder: useCustomIds ? indexOf(customIdArray, p.id) : undefined,
        } as IBudgetDashboardCardItem;
      });
      return await Promise.all(promises).then((queryResults) => {
        return queryResults;
      });
    } catch (error) {
      toast({ label: t('Something went wrong. Please try again later.') });
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getProjects,
    getAll,
    timeRange.startTime,
    timeRange.endTime,
    getAllTime,
    getLatestBudgetHoursData,
    getLatestBudgetCostData,
    toast,
  ]);
}
